import { Row } from 'components/Layout';
import TransactionAllocator from 'components/TransactionAllocator';
import { useLayoutContext } from 'contexts/LayoutContext';
import { UnrealisedFXGainOrLossSubTypeLabels } from 'further-ui/labels';
import { numberToCurrencyString } from 'further-ui/utils';
import useTransactionTypeSelectorOptions from 'hooks/form/useTransactionTypeSelectorOptions';
import { useState } from 'react';
import PayablesOrReceivablesHistory from './PayablesOrReceivablesHistory';
import getUnrealisedFxDisplayAmount from './getUnrealisedFxDisplayAmount';
import Table, { Column as TableColumn } from 'further-ui/components/Table';
import { styled } from '@mui/material/styles';
import { Column } from 'further-ui/components/Layout';
import usePostUnrealisedFxGainsData from './usePostUnrealisedFxGainsData';

const ExpandedRowContainer = styled('div')(({ theme }) => ({
  backgroundColor: theme.palette.grey[50],
  borderTop: `1px solid ${theme.palette.grey[300]}`,
}));

type Props = {
  onViewRecord?: (recordId: string) => void;
};

const ConfirmedPayablesOrReceivables: React.FC<Props> = ({ onViewRecord }) => {
  const { vehicle } = useLayoutContext();
  const {
    confirmedPayablesData,
    confirmedPayablesLoading,
    confirmedPayablesPagination,
    periodId,
  } = usePostUnrealisedFxGainsData();
  const [expandedRowId, setExpandedRowId] = useState<string | null>(null);
  const { transactionTypeOptions, isLoading: transactionTypesLoading } =
    useTransactionTypeSelectorOptions();

  if (!vehicle) {
    return null;
  }

  const confirmedRows = confirmedPayablesData?.results ?? [];

  const confirmedColumns: Array<TableColumn<(typeof confirmedRows)[number]>> = [
    {
      key: 'ledgerDate',
      label: 'Date',
      render: (row) => row.ledgerDate.toLabel(),
      disableSorting: true,
    },
    {
      key: 'counterpartyLabel',
      label: 'Counterparty',
      render: (row) => row.counterpartyLabel ?? '-',
      disableSorting: true,
    },
    {
      key: 'gainOrLossSubType',
      label: 'Type',
      render: (row) =>
        UnrealisedFXGainOrLossSubTypeLabels[row.gainOrLossSubType] ?? '-',
      disableSorting: true,
    },
    {
      key: 'amountLcy',
      label: 'Amount (LCY)',
      render: (row) =>
        numberToCurrencyString(
          getUnrealisedFxDisplayAmount(row.gainOrLossSubType, row.amountLcy),
          {
            fallback: '-',
            currency: vehicle.baseCurrency,
            negativeInBrackets: true,
          },
        ),
      disableSorting: true,
    },
  ];

  return (
    <Column spacing="lg">
      <Table
        columns={confirmedColumns}
        rows={confirmedRows}
        isLoading={confirmedPayablesLoading}
        pagination={confirmedPayablesPagination}
        rowsTotalCount={confirmedPayablesData?.totalCount ?? 0}
        isExpandableRow={() => true}
        isExpandedRow={(row) => expandedRowId === row.id}
        onRowExpand={(row) =>
          setExpandedRowId((prev) => (prev === row.id ? null : row.id))
        }
        getExpandedContent={(row) => (
          <ExpandedRowContainer>
            <Row fullWidth>
              {!transactionTypesLoading && (
                <TransactionAllocator
                  darkBackground
                  readOnly
                  transactionTypeOptions={transactionTypeOptions}
                  transactions={row.transactions}
                  transactionCurrency={row.currencyCode}
                  vehicle={vehicle}
                  fxRate={row.currencyRate ?? null}
                />
              )}
            </Row>
          </ExpandedRowContainer>
        )}
      />
      <PayablesOrReceivablesHistory
        vehicleId={vehicle._id}
        periodId={periodId}
        onViewRecord={onViewRecord}
      />
    </Column>
  );
};

export default ConfirmedPayablesOrReceivables;
